import {
  DragEventHandler,
  FC,
  FormEventHandler,
  lazy,
  memo,
  ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import { createPortal } from "react-dom"

import { Outlet, useLocation, useNavigate } from "react-router"

import { useAuth0 } from "@auth0/auth0-react"
import createStyle from "@josephmark/createstyle"
import * as RDropdownMenu from "@radix-ui/react-dropdown-menu"
import * as RPopover from "@radix-ui/react-popover"
import clsx from "clsx"
import * as m from "motion/react-m"
import { useSWRConfig } from "swr"
import { unstable_serialize } from "swr/infinite"

import Analytics from "@/lib/Analytics"
import { captureException } from "@/lib/Tracing"

import confirm from "@/utils/confirm"
import { s } from "@/utils/i18n"
import THREAD_COLORS from "@/utils/THREAD_COLORS"

import useAppSync from "@/hooks/useAppSync"
import useDragType from "@/hooks/useDragType"
import useLocalStorage from "@/hooks/useLocalStorage"
import useModal from "@/hooks/useModal"
import useProfile from "@/hooks/useProfile"
import useQueryPaged, { makeGetKey } from "@/hooks/useQueryPaged"
import useSidebarHidden from "@/hooks/useSidebarHidden"
import useToast from "@/hooks/useToast"

import Avatar from "@/components/Avatar"
import { Button } from "@/components/Button"
import DropdownMenu, { MenuContent, MenuItemBase } from "@/components/DropdownMenu"
import InlineInput from "@/components/form/InlineInput"
import { Input } from "@/components/form/Input"
import Image from "@/components/Image"
import Link from "@/components/Link"
import Sidebar from "@/components/Sidebar"
import { LineSkele } from "@/components/Skeletons"
import Tooltip from "@/components/Tooltip"
import { H3, H5, P2 } from "@/components/TypeRamp"

import ArrowUpIcon from "@/icons/ArrowUp"
import CalendarIcon from "@/icons/Calendar"
import ContextIcon from "@/icons/Context"
import ExpanderRightIcon from "@/icons/ExpanderRight"
import PlusIcon from "@/icons/Plus"
import ProxyCrossIcon from "@/icons/ProxyCross"
import SidebarIcon from "@/icons/Sidebar"

import { createProject, createStream, deleteStream, updateProject, updateStream } from "@/graphql/mutations"
import { streamsByUserProfileIdAndCreatedAt, projectsByUserProfileIdAndCreatedAt, getProject } from "@/graphql/queries"

import { Project, Stream } from "@/API"

import { DragTypes } from "@/context/DragTypeContext"
// eslint-disable-next-line import/no-cycle
import { HeaderRenderer } from "@/views"

const Select = lazy(() => import("@/components/form/Select"))

const getUnixTimestamp = () => Math.floor(Date.now() / 1000)

const SidebarButton = createStyle(
  "a",
  clsx(
    H5.classNames,
    "dark:aria-[current=page]:bg-slate dark:hover:bg-slate/60 flex items-center gap-1 rounded-lg px-1.5 py-2.5 transition hover:bg-black/5 aria-[current=page]:bg-black/5"
  )
)
const ProjectCard = createStyle(
  "a",
  clsx(
    "mx-1.5 flex w-full items-center gap-2 py-1.5",
    "dark:hover:bg-slate/60 rounded-lg hover:bg-black/5",
    "dark:aria-[current=page]:bg-slate aria-[current=page]:bg-black/5",
    "transition",
    "truncate overflow-hidden",
    P2.classNames
  )
)
// const NavButton = createStyle("a", "flex-1 rounded-sm flex justify-center p-3")

const DEFAULT_STREAM_ID = "uncategorized"

const useStreamProjectData = () => {
  const { userProfileId } = useProfile()
  const { mutate: globalMutate } = useSWRConfig()
  const { data: streamsData } = useQueryPaged<Stream[]>(
    "streamsByUserProfileIdAndCreatedAt",
    streamsByUserProfileIdAndCreatedAt,
    { userProfileId },
    {
      revalidateOnFocus: false,
      // suspense: true,
    }
  )
  const { data: projectsData } = useQueryPaged<Project[]>(
    "projectsByUserProfileIdAndCreatedAt",
    projectsByUserProfileIdAndCreatedAt,
    { userProfileId, filter: { enabled: { eq: true } } },
    {
      revalidateOnFocus: false,
      // suspense: true,
    }
  )

  const streams = useMemo(() => {
    const streamMap =
      (projectsData || []).reduce<Record<string, Project[]>>(
        (acc, _project) => {
          const project = _project as Project
          const stream = streamsData.find((_s) => _s!.id === project.streamId)
          const key = stream?.id || DEFAULT_STREAM_ID
          if (!acc[key]) {
            acc[key] = []
          }
          acc[key].push(project)
          return acc
        },
        Object.fromEntries(streamsData.map((stream) => [stream?.id || "", []]) || [])
      ) || {}

    return Object.entries(streamMap).map(([streamId, projects]) => ({
      stream:
        streamsData?.find((_s) => _s?.id === streamId) ||
        ({
          id: DEFAULT_STREAM_ID,
          color: "#ee450f",
          name: s`Uncategorised`,
          aiTitle: s`Uncategorised`,
        } as Stream),
      projects,
    }))
  }, [projectsData, streamsData])

  useLayoutEffect(() => {
    streams?.forEach(({ stream, projects }) => {
      projects?.forEach((project) => {
        globalMutate([getProject, { id: project?.id }], { getProject: { ...project, stream } })
      })
    })
  }, [globalMutate, streams])

  return {
    streams,
  }
}

const NewProjectMenu: FC<{ defaultStream?: string; label?: ReactNode }> = ({ defaultStream, label = s`Thread` }) => {
  const { streams } = useStreamProjectData()
  const { userProfileId, proxyId } = useProfile()
  const navigate = useNavigate()
  const { addToast } = useToast()
  const { mutation } = useAppSync()
  const { mutate: globalMutate } = useSWRConfig()
  const [isOpen, setIsOpen] = useState(false)
  const [portalNode, setPortalNode] = useState<HTMLDivElement | null>(null)

  useEffect(() => {
    const el = document.createElement("div")
    document.body.appendChild(el)
    setPortalNode(el)
    return () => {
      if (el && el.parentNode) {
        el.parentNode.removeChild(el)
      }
      setPortalNode(null)
    }
  }, [])

  const onSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    async (e) => {
      e.preventDefault()
      try {
        const formData = new FormData(e.currentTarget)

        const project = await mutation({
          query: createProject,
          variables: {
            input: {
              userProfileId,
              proxyId,
              streamId: formData.get("streamId") as string,
              name: formData.get("name") as string,
              title: formData.get("name") as string,
              color: THREAD_COLORS[Math.floor(Math.random() * THREAD_COLORS.length)].value,
              description: "",
              enabled: true,
              createdAt: getUnixTimestamp(),
              updatedAt: getUnixTimestamp(),
            },
          },
        })

        globalMutate(
          unstable_serialize(
            makeGetKey("projectsByUserProfileIdAndCreatedAt", projectsByUserProfileIdAndCreatedAt, {
              userProfileId,
              filter: { enabled: { eq: true } },
            })
          )
        )

        Analytics?.track?.("Created Project", { type: "manual" })
        navigate(`/dashboard/${project!.createProject!.id}`)
        setIsOpen(false)
      } catch (err: any) {
        console.error(err)
        err.message = "Error creating new project"
        captureException(err)
        addToast(err, "error")
      }
    },
    [addToast, globalMutate, mutation, navigate, proxyId, userProfileId]
  )
  return (
    <RDropdownMenu.Item asChild>
      <RPopover.Root onOpenChange={setIsOpen} open={isOpen} modal>
        <RPopover.Trigger className={MenuItemBase.classNames}>{label}</RPopover.Trigger>
        <RPopover.Portal>
          <RPopover.Content
            side="right"
            sideOffset={10}
            align="start"
            className={clsx(MenuContent.classNames, "z-40 px-4 py-5")}
            style={{ boxShadow: "4px 8px 10px 0 rgba(0,0,0, 0.10)" }}
          >
            <form onSubmit={onSubmit} className="flex flex-col">
              <H5 className="mb-4">{s`New Thread`}</H5>
              <Input placeholder={s`Thread Name`} className="mb-1" required name="name" />
              <Suspense fallback={<LineSkele style={{ height: 42 }} />}>
                <Select
                  options={streams.map(({ stream }) => ({ value: stream.id, label: stream.name }))}
                  required
                  name="streamId"
                  defaultValue={defaultStream}
                  placeholder={s`Select Stream`}
                />
              </Suspense>
              <Button as="button" theme="secondary" size="small" className="mt-4 place-self-center">{s`Create`}</Button>
            </form>
            {isOpen && portalNode && createPortal(<div className="fixed inset-0 z-30 bg-black/10" />, portalNode)}
          </RPopover.Content>
        </RPopover.Portal>
      </RPopover.Root>
    </RDropdownMenu.Item>
  )
}

// Orange: "#ee450f",
// Green: "#24ff39",
// Purple: "#ed4dff",
// Blue: "#00d0ff",
// Yellow: "#ffdd00",

const NavStream: FC<{ stream: Stream; projects: Project[] }> = ({ stream, projects }) => {
  const { mutation } = useAppSync()
  const { userProfileId } = useProfile()
  const { addToast } = useToast()
  const { pathname } = useLocation()
  const { mutate: globalMutate } = useSWRConfig()
  const { dragType, setDragType } = useDragType()

  const [isCollapsed, setIsCollapsed] = useLocalStorage(`stream-${stream.id}-collapsed`, false)
  const [isRenaming, setIsRenaming] = useState(false)
  const [dragCounter, setDragCounter] = useState(0)
  const nameInputRef = useRef<HTMLInputElement>(null)

  const projectListId = [stream.id, "project_list"].join("-")

  const onSubmitNameForm = useCallback<FormEventHandler<HTMLFormElement>>(
    async (e) => {
      e.preventDefault()
      const formData = new FormData(e.currentTarget)
      try {
        await mutation({
          query: updateStream,
          variables: {
            input: {
              id: stream.id,
              userProfileId,
              name: formData.get("name") as string,
              updatedAt: getUnixTimestamp(),
            },
          },
        })
        globalMutate(
          unstable_serialize(
            makeGetKey("streamsByUserProfileIdAndCreatedAt", streamsByUserProfileIdAndCreatedAt, { userProfileId })
          )
        )
      } catch (err: any) {
        console.error(err)
        err.message = "Error updating stream name"
        captureException(err)
        addToast(err, "error")
      }
      setIsRenaming(false)
    },
    [addToast, globalMutate, mutation, stream.id, userProfileId]
  )

  const onClickDeleteStream = useCallback(async () => {
    try {
      if (
        !(await confirm(s`Delete stream`, {
          message: s`Are you sure you want to delete this stream? This action cannot be undone.`,
          flavour: "delete",
        }))
      ) {
        return
      }
      await mutation({
        query: deleteStream,
        variables: {
          input: {
            id: stream.id!,
            userProfileId,
          },
        },
      })
      globalMutate(
        unstable_serialize(
          makeGetKey("streamsByUserProfileIdAndCreatedAt", streamsByUserProfileIdAndCreatedAt, { userProfileId })
        )
      )
    } catch (err: any) {
      console.error(err)
      err.message = "Error deleting stream"
      captureException(err)
      addToast(err, "error")
    }
  }, [addToast, globalMutate, mutation, stream.id, userProfileId])

  const onDragStartProject = useCallback<DragEventHandler>(
    (e) => {
      e.dataTransfer.setData("text/plain", e.currentTarget.getAttribute("data-project-id") || "")
      setDragType(DragTypes.PROJECT)
    },
    [setDragType]
  )

  const onDragEnter = useCallback<DragEventHandler>((e) => {
    e.preventDefault()
    const streamId = e.currentTarget.getAttribute("data-stream-id")
    if (!streamId) {
      return
    }
    // Increment the drag counter for the project
    setDragCounter((prev) => prev + 1)
  }, [])
  const onDragLeave = useCallback<DragEventHandler>((e) => {
    e.preventDefault()
    const streamId = e.currentTarget.getAttribute("data-stream-id")
    if (!streamId) {
      return
    }
    // Decrement the drag counter for the project
    setDragCounter((prev) => prev - 1)
  }, [])
  const onDragOver = useCallback<DragEventHandler>((e) => e.preventDefault(), [])

  // Handle drag+dropping tasks onto a project
  const onDropInStream = useCallback<DragEventHandler>(
    async (e) => {
      const streamId = e.currentTarget.getAttribute("data-stream-id")
      setDragType(null)
      setDragCounter(0)
      if (dragType !== DragTypes.PROJECT || !streamId) {
        return
      }
      e.preventDefault()

      await mutation({
        query: updateProject,
        variables: {
          input: {
            id: e.dataTransfer.getData("text/plain"),
            streamId,
            userProfileId,
            updatedAt: getUnixTimestamp(),
          },
        },
      })

      globalMutate(
        unstable_serialize(
          makeGetKey("projectsByUserProfileIdAndCreatedAt", projectsByUserProfileIdAndCreatedAt, {
            userProfileId,
            filter: { enabled: { eq: true } },
          })
        )
      )
    },
    [dragType, globalMutate, mutation, setDragType, userProfileId]
  )

  return (
    <div
      className={clsx(
        "border-bonjour dark:border-slate mr-2 ml-2",
        dragCounter > 0 && "rounded-sm bg-black/15 shadow-[0_0_5px_-1px_var(--stream-color)] dark:bg-white/15"
      )}
      data-stream-id={stream.id}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDropInStream}
    >
      {/* collapse/expand toggle */}
      <m.div
        className="group flex items-center pr-2"
        animate={{ marginBottom: isCollapsed ? 0 : "0.625rem" }}
        initial={false}
      >
        <button
          type="button"
          value={stream.id}
          onClick={() => setIsCollapsed((prev) => !prev)}
          className="flex h-4 w-4 cursor-pointer items-center justify-center self-center transition hover:opacity-60"
          aria-expanded={isCollapsed}
          aria-controls={projectListId}
        >
          <span className="sr-only">{isCollapsed ? s`Expand threads` : s`Collapse threads`}</span>
          <ExpanderRightIcon width="6" height="6" className={clsx("transition", !isCollapsed ? "rotate-90" : "")} />
        </button>
        <H5 as="h3" className={clsx(isRenaming && "hidden", "grow truncate")}>
          {stream.name || stream?.aiTitle}
        </H5>
        {isRenaming && (
          <form className={clsx("ml-0.5 grow")} onSubmit={onSubmitNameForm}>
            <InlineInput
              className={clsx(H5.classNames)}
              ref={nameInputRef}
              required
              name="name"
              defaultValue={stream.name || stream.aiTitle || ""}
              onBlur={(e) => (e.currentTarget as any)?.form?.requestSubmit()}
            />
          </form>
        )}
        {stream.id !== DEFAULT_STREAM_ID && (
          <DropdownMenu
            className="-my-0.5 opacity-0 group-hover:opacity-100 aria-expanded:opacity-100"
            actions={[
              {
                label: s`Rename`,
                as: "button",
                onClick: () => {
                  setIsRenaming(true)
                  requestAnimationFrame(() => requestAnimationFrame(() => nameInputRef.current?.focus()))
                },
              },
              <NewProjectMenu label={s`New Thread`} key="new" defaultStream={stream.id} />,
              "divider",
              {
                label: s`Delete`,
                as: "button",
                type: "button",
                theme: "destructive" as const,
                onClick: onClickDeleteStream,
              },
            ]}
            align="start"
          />
        )}
      </m.div>
      <m.ul
        className={clsx("flex", "mb-6 flex-col gap-0.5 overflow-hidden")}
        aria-hidden={isCollapsed}
        animate={{ height: isCollapsed ? 0 : "auto" }}
        initial={false}
        id={projectListId}
      >
        {projects?.map((project) => (
          <li key={project?.id} className="flex">
            <Tooltip tip={project?.title || project?.aiTitle || ""} side="right">
              <Link
                href={`/dashboard/${project?.id}/`}
                className={clsx(ProjectCard.classNames)}
                aria-current={pathname.startsWith(`/dashboard/${project?.id}`) ? "page" : undefined}
                data-project-id={project?.id}
                draggable
                onDragStart={onDragStartProject}
              >
                <span
                  className="w-px shrink-0 self-stretch rounded-r-sm"
                  style={{ backgroundColor: project.color || "#ee450f" }}
                />
                {project?.title || project?.aiTitle}
              </Link>
            </Tooltip>
          </li>
        ))}
      </m.ul>
    </div>
  )
}

export const NavLayout: FC<{ children?: ReactNode }> = ({ children }) => {
  const { logout } = useAuth0()
  const { userProfile, userProfileId } = useProfile()
  const { mutation } = useAppSync()
  const { pathname } = useLocation()
  const { addToast } = useToast()
  const { isModalActive } = useModal()
  const { streams } = useStreamProjectData()
  const { mutate: globalMutate } = useSWRConfig()

  const inputRef = useRef<HTMLInputElement>(null)

  const [_platform] = useState(() => window.navigator.platform)
  const [newStreamOpen, setNewStreamOpen] = useState(false)
  const [sidebarHidden, setSidebarHidden] = useSidebarHidden()

  const onClickCreateNewStream = useCallback(() => {
    setNewStreamOpen(true)
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        inputRef.current?.focus()
      })
    )
  }, [])

  const onSubmitNewStream = useCallback<FormEventHandler<HTMLFormElement>>(
    async (e) => {
      e.preventDefault()
      const formData = new FormData(e.currentTarget)
      const focusInput = (formData.get("stream_name") as string)?.trim()
      if (!focusInput) {
        return
      }
      try {
        e.currentTarget.reset()
        await mutation({
          query: createStream,
          variables: {
            input: {
              name: focusInput,
              aiTitle: focusInput,
              userProfileId,
              pluginIds: [],
              enabled: true,
              createdAt: getUnixTimestamp(),
              updatedAt: getUnixTimestamp(),
              color: "#ee450f", // default color, can be changed later
            },
          },
        })
        Analytics?.track?.("Created Stream", { type: "manual" })
        globalMutate(
          unstable_serialize(
            makeGetKey("streamsByUserProfileIdAndCreatedAt", streamsByUserProfileIdAndCreatedAt, { userProfileId })
          )
        )
      } catch (err: any) {
        console.error(err)
        err.message = "Error creating new stream"
        captureException(err)
        addToast(err, "error")
      }
    },
    [addToast, globalMutate, mutation, userProfileId]
  )

  return (
    <>
      <div className="flex min-h-screen flex-col justify-end gap-8 pt-14 text-center md:hidden">
        <div className="h-0 grow px-6">
          <Image
            src="/assets/proxy-app-mock.png"
            className="mx-auto object-contain"
            style={{ maxWidth: "80%", height: "100%", aspectRatio: 231 / 478 }}
          />
        </div>
        <div className="relative px-6 pb-5">
          <H3 className="mb-2.5">{s`Proxy Companion App`}</H3>
          <P2 className="mb-8">{s`To use Proxy on your phone, download the companion app or open Proxy on your desktop.`}</P2>
          <Button
            theme="secondary"
            size="xlarge"
            className="w-full"
            href="https://testflight.apple.com/join/H56A7bA7"
            target="_blank"
            rel="noopener noreferrer"
          >{s`Download the app`}</Button>
          <P2 className="relative mt-5">{s`Available on iOS and Android`}</P2>
        </div>
      </div>
      <div className="hidden min-h-screen max-w-full grow md:flex">
        <m.div initial={false} animate={{ width: sidebarHidden ? "0px" : "250px" }} inert={isModalActive}>
          <Sidebar
            initial={false}
            animate={{ x: sidebarHidden ? "-100%" : "0", visibility: sidebarHidden ? "hidden" : "visible" }}
            style={{ position: sidebarHidden ? "absolute" : "sticky" }}
          >
            <header className="mb-2.5 flex items-center gap-1 p-3">
              <ProxyCrossIcon
                width="24"
                height="24"
                className={clsx(
                  "ml-1",
                  import.meta.env.VITE_PUBLIC_ENVIRONMENT === "dev" && "text-orange invert",
                  import.meta.env.VITE_PUBLIC_ENVIRONMENT === "staging" && "text-orange"
                )}
              />
              {import.meta.env.VITE_PUBLIC_ENVIRONMENT === "dev" && (
                <H5 as="span" className="text-orange uppercase invert">{s`Dev`}</H5>
              )}
              {import.meta.env.VITE_PUBLIC_ENVIRONMENT === "staging" && (
                <H5 as="span" className="text-orange uppercase">{s`Staging`}</H5>
              )}
              <div className="grow" />
              <Tooltip tip={s`Hide sidebar`} side="right">
                <button
                  type="button"
                  onClick={() => setSidebarHidden((prev) => !prev)}
                  className="dark:hover:bg-charcoal cursor-pointer rounded-sm p-1 transition hover:bg-black/5"
                  tabIndex={sidebarHidden ? -1 : undefined}
                >
                  <SidebarIcon height="20" width="20" />
                  <span className="sr-only">{s`Hide sidebar`}</span>
                </button>
              </Tooltip>
            </header>
            <ul className="flex flex-col gap-1 px-3">
              <li>
                <Link
                  href="/calendar"
                  aria-current={pathname.includes("calendar") ? "page" : undefined}
                  className={clsx(SidebarButton.classNames)}
                >
                  <CalendarIcon /> {s`Calendar`}
                </Link>
              </li>
              <li>
                <Link
                  href="/context"
                  aria-current={pathname.startsWith("/context") ? "page" : undefined}
                  className={clsx(SidebarButton.classNames)}
                >
                  <ContextIcon /> {s`Context`}
                </Link>
              </li>
              <li>
                <DropdownMenu
                  theme="custom"
                  className={clsx(
                    SidebarButton.classNames,
                    "dark:aria-expanded:bg-slate w-full aria-expanded:bg-black/5"
                  )}
                  align="start"
                  contentProps={{
                    style: {
                      width: "var(--radix-dropdown-menu-trigger-width)",
                      boxShadow: "0 4px 8px 0 rgba(0,0,0, 0.08)",
                    },
                  }}
                  actions={[
                    {
                      label: s`Stream`,
                      as: "button",
                      onClick: onClickCreateNewStream,
                    },
                    <NewProjectMenu />,
                  ]}
                >
                  <PlusIcon strokeWidth="1.1" width="16" height="16" className="mx-0.5" /> {s`Create new`}
                </DropdownMenu>
              </li>
            </ul>
            <div className="scrollbar-hidden h-0 grow overflow-auto pt-4">
              {streams.map(({ stream, projects }) => (
                <NavStream key={stream.id} stream={stream} projects={projects} />
              ))}
              <form
                className={clsx(newStreamOpen ? "block" : "hidden", "relative mx-2.5")}
                onSubmit={onSubmitNewStream}
              >
                <input
                  className="bg-empress/10 w-full rounded-xl py-2.5 pr-10 pl-1.5"
                  ref={inputRef}
                  name="stream_name"
                  onBlur={(e) => {
                    if (!e.currentTarget.value.trim()) {
                      setNewStreamOpen(false)
                    }
                  }}
                  placeholder={s`Stream name`}
                  required
                />
                <button
                  type="submit"
                  className="absolute top-1/2 right-2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-black text-white disabled:opacity-20"
                >
                  <ArrowUpIcon />
                  <span className="sr-only">{s`Add new Stream`}</span>
                </button>
              </form>
            </div>
            <div className="sticky bottom-0 px-3 py-4">
              <DropdownMenu
                className="dark:aria-expanded:bg-charcoal dark:hover:bg-charcoal flex w-full cursor-pointer items-center gap-2 rounded-full p-1 transition hover:bg-black/5 aria-expanded:bg-black/5"
                theme="custom"
                align="start"
                actions={[
                  {
                    label: s`Settings`,
                    href: "/settings",
                  },
                  "divider",
                  {
                    label: s`Log Out`,
                    theme: "destructive",
                    onClick: () => logout({ logoutParams: { returnTo: window.location.origin } }),
                    as: "button",
                    type: "button",
                  },
                ]}
              >
                <Avatar user={userProfile!} size="32" />
                <H5 as="span" className="w-0 grow truncate text-left">
                  {userProfile?.name}
                </H5>
              </DropdownMenu>
            </div>
          </Sidebar>
        </m.div>
        <div className="flex grow flex-col">
          <HeaderRenderer />
          <div className="flex grow">{children || <Outlet />}</div>
        </div>
      </div>
    </>
  )
}

export default memo(NavLayout)
